/**
 * Composant : Suggestion TVA via IA (agent comptable)
 * Bouton qui ouvre un dialog, demande à l'IA un taux + référence légale,
 * affiche la suggestion, et permet à l'utilisateur de l'appliquer.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sparkles, Loader2, BookOpen, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SuggestTVAButton({ description, contexteActivite = 'agriculture', typeClient, montantHt, onApply, size = 'default', variant = 'outline' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const fetchSuggestion = async () => {
    if (!description || description.length < 3) {
      toast.error('Saisissez la description du produit/prestation avant');
      return;
    }
    setOpen(true);
    setLoading(true);
    setSuggestion(null);
    try {
      const r = await api.post('/tva-ia/suggerer', {
        description,
        contexte_activite: contexteActivite,
        type_client: typeClient,
        montant_ht: montantHt,
      });
      setSuggestion(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'IA indisponible');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const apply = (taux) => {
    onApply?.(taux);
    setOpen(false);
    toast.success(`Taux ${taux}% appliqué`);
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={fetchSuggestion} className="gap-1" title="Suggérer TVA (IA)" data-testid="btn-suggest-tva">
        <Sparkles className="w-3.5 h-3.5" />
        {size !== 'icon' && <span className="text-xs">Suggérer TVA (IA)</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-suggest-tva">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Suggestion TVA — Agent comptable IA
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /><p className="mt-2 text-sm text-muted-foreground">L'IA analyse...</p></div>
          ) : suggestion ? (
            <div className="space-y-3">
              <div className="bg-muted/50 p-3 rounded">
                <p className="text-xs text-muted-foreground mb-1">Description analysée :</p>
                <p className="text-sm italic">"{description}"</p>
              </div>

              <div className="bg-green-50 dark:bg-green-950/20 border border-green-300 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Taux suggéré :</p>
                  <Badge className="text-lg px-3 py-1">{suggestion.taux_suggere}%</Badge>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-blue-500" />
                  <p className="text-sm font-mono">{suggestion.article_loi}</p>
                </div>
                <p className="text-sm">{suggestion.justification}</p>
                {suggestion.niveau_confiance && (
                  <p className="text-xs mt-2">
                    Confiance IA : <Badge variant="outline">{suggestion.niveau_confiance}</Badge>
                  </p>
                )}
              </div>

              {(suggestion.alternatives || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1">Alternatives possibles :</p>
                  <div className="space-y-2">
                    {suggestion.alternatives.map((alt, i) => (
                      <div key={i} className="border rounded p-2 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm"><strong>{alt.taux}%</strong> — {alt.condition}</p>
                          {alt.article && <p className="text-xs text-muted-foreground font-mono">{alt.article}</p>}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => apply(alt.taux)}>Appliquer</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-xs">
                  {suggestion.rappel_validation || "Suggestion indicative. Validez avec votre expert-comptable pour votre situation précise."}
                </AlertDescription>
              </Alert>
            </div>
          ) : null}
          <DialogFooter>
            {suggestion && (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={() => apply(suggestion.taux_suggere)} data-testid="btn-apply-tva">
                  Appliquer {suggestion.taux_suggere}%
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
